import React, { Fragment, useContext, useState } from "react";
import { equals } from "ramda";
import {
  OrganizeViews,
  isAnthologyView,
  isContentGroupsHome,
  useView,
} from "../../organizeUtil";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAngleRight,
  faCircleMinus,
  faFolderPlus,
  faCopy,
  faTrashCan,
  faPen,
} from "@fortawesome/pro-regular-svg-icons";
import {
  IncludeDialogTypes,
  isAnthology,
  isCollection,
} from "../../organizeUtil";
import { OrganizeContext } from "../../organize-context";
import {
  useAnthologyDelete,
  useAnthologyUpdate,
} from "../../../anthologies/anthologyServices";
import {
  useCollectionDelete,
  useCollectionUpdate,
} from "../../../collections/collectionServices";
import { GroupBrowserContext } from "../../OrganizeView";

import DeleteDialog from "../../../common/DeleteDialog";
import Divider from "@material-ui/core/Divider";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import MenuItem from "@material-ui/core/MenuItem";

import IncludedInMenu from "../../common/IncludedInMenu";
import { useMediaPermissions } from "../../../../util/mediaUtil";
import { useAlert } from "../../OrganizeProvider";
import { useHistory, useLocation } from "react-router-dom";
import { notBlank } from "../../../../util/ext";

// if child is collection and view is anthology, then only move
// if content group home, only copy

const OtherActions = React.forwardRef((props, ref) => {
  const { child, ancestors, handleRemoveClick } = props;
  const view = useView();
  const alert = useAlert();
  const browser = useContext(GroupBrowserContext);
  const location = useLocation();
  const history = useHistory();
  const { updateIncludeDialog } = useContext(OrganizeContext);
  const openIncludeDialog = action => event => {
    event.stopPropagation();
    updateIncludeDialog({
      open: true,
      action: action,
      activeType: IncludeDialogTypes.contentGroup,
      selected: [child],
    });
  };

  const { deletable, readOnly } = useMediaPermissions(child);

  const [anchorEl, setAnchorEl] = useState(null);

  const handleIncludedInClick = event => {
    event.stopPropagation();
    event.preventDefault();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = event => {
    event.stopPropagation();
    setAnchorEl(null);
  };

  const handleEditClick = () => {
    const { pathname, search } = location;
    const fromPath = notBlank(search) ? pathname + search : pathname;
    const mediaType = isAnthology(child) ? "anthologies" : "collections";
    history.push(`/creator/${mediaType}/${child.id}`, {
      from: fromPath,
    });
  };

  const updateAnthology = useAnthologyUpdate();
  const updateCollection = useCollectionUpdate();
  const handleUndo = previousChild => async () => {
    browser.setLoading(true);
    const mutation = {
      resourceIds: { id: previousChild.id },
      data: {
        status: previousChild?.status ?? null,
      },
    };
    try {
      if (isAnthology(previousChild)) {
        await updateAnthology.mutateAsync(mutation);
      }

      if (isCollection(previousChild)) {
        await updateCollection.mutateAsync(mutation);
      }
    } catch (err) {
      console.log(err);
    }
    browser.setLoading(false);
  };

  //-- Manage Content Group Delete
  const deleteAnthology = useAnthologyDelete();
  const deleteCollection = useCollectionDelete();
  const handleDelete = async () => {
    if (isAnthology(child)) {
      await deleteAnthology.mutateAsync({ anthology: child });
    }

    if (isCollection(child)) {
      await deleteCollection.mutateAsync({ collection: child });
    }

    alert.update({
      actionButtonLabel: "undo",
      showAction: true,
      onAlertClick: handleUndo(child),
    });
  };

  return equals(view, OrganizeViews.UNGROUPED_ITEMS) ? (
    <Fragment>
      {!readOnly && (
        <MenuItem
          ref={ref}
          onClick={event => {
            event.stopPropagation();
            handleEditClick();
          }}
        >
          <ListItemIcon>
            <FontAwesomeIcon icon={faPen} />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
      )}

      <MenuItem ref={ref} onClick={openIncludeDialog("move")}>
        <ListItemIcon>
          <FontAwesomeIcon icon={faFolderPlus} />
        </ListItemIcon>
        <ListItemText>Move</ListItemText>
      </MenuItem>

      {deletable && (
        <DeleteDialog
          handleDelete={handleDelete}
          renderButton={onDelete => (
            <MenuItem
              ref={ref}
              onClick={event => {
                event.stopPropagation();
                onDelete();
              }}
            >
              <ListItemIcon>
                <FontAwesomeIcon icon={faTrashCan} />
              </ListItemIcon>
              <ListItemText>Delete</ListItemText>
            </MenuItem>
          )}
        />
      )}
    </Fragment>
  ) : (
    <Fragment>
      <MenuItem ref={ref} onClick={handleIncludedInClick}>
        <ListItemText style={{ marginRight: 15 }}>Included In</ListItemText>
        <FontAwesomeIcon icon={faAngleRight} />
        <IncludedInMenu
          parents={ancestors}
          child={child}
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          handleClose={handleClose}
          anchorOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
        />
      </MenuItem>
      <Divider />

      {!readOnly && (
        <MenuItem
          ref={ref}
          onClick={event => {
            event.stopPropagation();
            handleEditClick();
          }}
        >
          <ListItemIcon>
            <FontAwesomeIcon icon={faPen} />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
      )}

      {!isContentGroupsHome(view) && !readOnly && (
        <MenuItem ref={ref} onClick={openIncludeDialog("move")}>
          <ListItemIcon>
            <FontAwesomeIcon icon={faFolderPlus} />
          </ListItemIcon>
          <ListItemText>Move</ListItemText>
        </MenuItem>
      )}

      {!isAnthologyView(view) && (
        <MenuItem ref={ref} onClick={openIncludeDialog("copy")}>
          <ListItemIcon>
            <FontAwesomeIcon icon={faCopy} />
          </ListItemIcon>
          <ListItemText>Copy</ListItemText>
        </MenuItem>
      )}

      {!equals(view, OrganizeViews.CONTENT_GROUPS) && !readOnly && (
        <MenuItem ref={ref} onClick={handleRemoveClick}>
          <ListItemIcon>
            <FontAwesomeIcon icon={faCircleMinus} />
          </ListItemIcon>
          <ListItemText>Remove</ListItemText>
        </MenuItem>
      )}

      {deletable && (
        <DeleteDialog
          handleDelete={handleDelete}
          renderButton={onDelete => (
            <MenuItem
              ref={ref}
              onClick={event => {
                event.stopPropagation();
                onDelete();
              }}
            >
              <ListItemIcon>
                <FontAwesomeIcon icon={faTrashCan} />
              </ListItemIcon>
              <ListItemText>Delete</ListItemText>
            </MenuItem>
          )}
        />
      )}
    </Fragment>
  );
});

export default OtherActions;
